import type { H3Event } from 'vinxi/http';

import { clearRecaptchaVerifyTokenFromCookie, ensureRecaptchaVerifyToken } from '~/utils/recaptchaVerify';
import { DEFAULT_LANG } from '~/constants';

/** Signature cookie name */
export const SIGNATURE_COOKIE_NAME = '_signature_';

/** Signature cookie max age (seconds) - 5 minutes */
export const SIGNATURE_MAX_AGE = 60 * 5;

/**
 * Helper: get header value (handles string | string[] types)
 */
function getHeader(value: string | string[] | undefined): string {
  if (Array.isArray(value)) return value[0] || '';
  return value || '';
}

/**
 * Fetch anti-crawler signature
 * @param nativeEvent - H3 event object (optional, for SSR)
 * @param customHeaders - Custom request headers (optional)
 * @param retryCount - Current retry count (internal use, prevents infinite recursion)
 * @returns Signature string, or null on failure
 */
export async function fetchSignature(
  nativeEvent?: H3Event,
  customHeaders?: Record<string, string>,
  retryCount: number = 0
): Promise<string | null> {
  const MAX_RETRY = 3; // Max retry attempts

  let requestHeaders: Record<string, string>;
  let origin: string;

  if (nativeEvent) {
    // SSR: get headers from nativeEvent
    const originalHeaders = nativeEvent.node.req.headers;

    // Build request headers - inherit key headers from client
    requestHeaders = {
      accept: 'application/json',
      'accept-language': getHeader(originalHeaders['accept-language']) || DEFAULT_LANG,
      'content-type': 'application/json',
      'x-client-type': import.meta.env.VITE_APP_CLIENT,
      'x-channel-type': getHeader(originalHeaders['x-channel-type']) || '0',
      'x-device-id': getHeader(originalHeaders['x-device-id']) || '',
      'x-front-session-id': getHeader(originalHeaders['x-front-session-id']) || '',
      ...customHeaders,
    };

    // Inherit cookies (important!)
    if (originalHeaders.cookie) {
      requestHeaders['cookie'] = getHeader(originalHeaders.cookie);
    }

    // Dynamically build origin (from request protocol and host)
    const protocol =
      getHeader(originalHeaders['x-forwarded-proto']) || (nativeEvent.node.req.connection as any).encrypted
        ? 'https'
        : 'http';
    const host = getHeader(originalHeaders.host);
    origin = `${protocol}://${host}`;
  } else {
    // Client-side: use window.location
    requestHeaders = {
      accept: 'application/json',
      'content-type': 'application/json',
      'x-client-type': import.meta.env.VITE_APP_CLIENT,
      ...customHeaders,
    };

    origin = `${window.location.protocol}//${window.location.host}`;
  }

  try {
    const response = await fetch(`${origin}/api/user/security/anti-crawler/init`, {
      method: 'POST',
      headers: requestHeaders,
    });
    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data?.serverSeed) {
        return result.data.serverSeed;
      }
      if (!result.success && String(result.errCode) === '11000091') {
        // Check if max retry attempts reached
        if (retryCount >= MAX_RETRY) {
          console.error('[fetchSignature] Max retry attempts reached, giving up.');
          return null;
        }

        // Need to regenerate reCAPTCHA verify token, clear related cookies
        console.warn(`[fetchSignature] Recaptcha token expired, retrying (${retryCount + 1}/${MAX_RETRY})...`);
        clearRecaptchaVerifyTokenFromCookie();
        const verifyToken = await ensureRecaptchaVerifyToken(origin, {
          deviceId: requestHeaders['X-Device-Id'] || '',
          channelType: String(requestHeaders['X-Channel-Type'] || '0'),
          sessionId: requestHeaders['X-Front-Session-Id'] || '',
          lang: setting.lang,
        });

        if (!verifyToken) {
          console.error('[fetchSignature] Failed to get verify token.');
          return null;
        }

        // Retry signature request (increment retry count)
        return fetchSignature(
          undefined,
          {
            ...customHeaders,
            'X-Recaptcha-Token': verifyToken,
          },
          retryCount + 1
        );
      }
    } else {
      console.error('[fetchSignature] Failed:', response.status, await response.text());
    }
  } catch (error) {
    console.error('[fetchSignature] Error:', error);
  }

  return null;
}
